import { Music, GraduationCap } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatTime } from "@/lib/dateUtils";
import { getLessonSchedule, type LessonFields } from "@/lib/lessonSchedule";

interface DanceStartLineProps {
  event: LessonFields;
  /** "compact" for the calendar's day columns, "full" for cards and modals. */
  variant?: "compact" | "full";
  className?: string;
}

/**
 * When the floor opens, for an event that opens with a class.
 *
 * Renders nothing for the ordinary case of a social with no lesson, where the
 * start time already answers the question. The point is the person deciding
 * between two dances in one night: the listing says seven, but if seven is a
 * beginners' class then the dancing they came for starts at eight, and that is
 * the number that decides whether both evenings fit.
 */
export function DanceStartLine({ event, variant = "compact", className }: DanceStartLineProps) {
  const schedule = getLessonSchedule(event);
  if (schedule.kind === "none") return null;

  const compact = variant === "compact";
  const iconSize = cn("flex-shrink-0", compact ? "w-3 h-3" : "w-3.5 h-3.5");
  // The cap means a class, the footsteps mean a floor. Keeping them apart is
  // the whole point of the line: one is what you sit through, the other is
  // what you came for.
  const lessonIcon = <GraduationCap className={iconSize} aria-hidden="true" />;
  const dancingIcon = <Music className={iconSize} aria-hidden="true" />;
  const base = cn(
    "flex items-center gap-1 min-w-0",
    compact ? "text-[10px]" : "text-xs",
    className
  );

  if (schedule.kind === "lesson-untimed") {
    return (
      <span className={cn(base, "text-muted-foreground")}>
        {lessonIcon}
        <span className="truncate">Lesson first</span>
      </span>
    );
  }

  if (schedule.kind === "lesson-only") {
    return (
      <span className={cn(base, "text-muted-foreground")}>
        {lessonIcon}
        <span className="truncate">Lesson only</span>
      </span>
    );
  }

  return (
    <span className={cn(base, "font-medium text-primary")}>
      {dancingIcon}
      <span className="truncate tabular-nums">
        {compact
          ? `Dancing ${formatTime(schedule.danceStart)}`
          : `Dancing from ${formatTime(schedule.danceStart)} (lesson first)`}
      </span>
    </span>
  );
}
